import React, { useEffect, useState, useMemo } from "react";
import withRouter from "components/Common/withRouter";
import TableContainer from "components/Common/TableContainer";
import {
  Card, CardBody, Col, Container, Row, Badge, Spinner, Button,
  Modal, ModalHeader, ModalBody, Form, FormGroup, Label, Input
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import axios from "axios";
import moment from "moment";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

const API_BASE = "https://maasradarapi.stechomeyazilim.info";

const JobPostsList = () => {
  document.title = "İş İlanları | Maaş Radar Admin Paneli";

  const [allItems, setAllItems] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [filter, setFilter] = useState("all"); // all, active, deleted, passive

  const [detailModal, setDetailModal] = useState(false);
  const [detailItem, setDetailItem] = useState(null);

  const [editModal, setEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});

  function getAuthHeaders() {
    const token = localStorage.getItem("token");
    return { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" };
  }

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/JobPosts?$orderby=CreatedAt desc&$top=200&$expand=Companies($select=Name),Cities($select=Name),Professions($select=Name)`, { headers: getAuthHeaders() });
      const data = res.data.value || [];
      setAllItems(data);
      applyFilter(data, filter);
    } catch (error) {
      console.error("Veriler alınamadı", error);
      setAllItems([]);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = (data, f) => {
    if (f === "all") setItems(data);
    else if (f === "active") setItems(data.filter(i => !i.IsDeleted && i.IsActive !== false));
    else if (f === "deleted") setItems(data.filter(i => i.IsDeleted));
    else if (f === "passive") setItems(data.filter(i => !i.IsDeleted && i.IsActive === false));
  };

  useEffect(() => { fetchItems(); }, []);
  useEffect(() => { applyFilter(allItems, filter); }, [filter]);

  const showToast = (success, message) => {
    toastr.options = { positionClass: "toast-top-right", timeOut: 5000, closeButton: true, progressBar: true };
    if (success) toastr.success(message, "Başarılı");
    else toastr.error(message, "Hata");
  };

  const handleDelete = (id) => {
    if (!window.confirm("Bu iş ilanını silmek istediğinize emin misiniz?")) return;
    setProcessing(p => ({ ...p, [id + "delete"]: true }));
    axios.patch(`${API_BASE}/JobPosts(${id})`, { IsDeleted: true }, { headers: getAuthHeaders() })
      .then(() => { showToast(true, "İş ilanı silindi."); fetchItems(); })
      .catch((err) => { console.error(err); showToast(false, "Silme sırasında hata oluştu."); })
      .finally(() => setProcessing(p => ({ ...p, [id + "delete"]: false })));
  };

  const openDetail = (item) => { setDetailItem(item); setDetailModal(true); };

  const openEdit = (item) => {
    setEditItem(item);
    setEditForm({
      PositionName: item.PositionName || "",
      CompanyName: item.Companies?.Name || "",
      City: item.Cities?.Name || "",
      Profession: item.Professions?.Name || "",
      Description: item.Description || "",
      IsActive: item.IsActive !== false,
      IsDeleted: item.IsDeleted === true,
    });
    setEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editItem) return;
    setProcessing(p => ({ ...p, [editItem.Id + "save"]: true }));
    try {
      await axios.patch(`${API_BASE}/JobPosts(${editItem.Id})`, editForm, { headers: getAuthHeaders() });
      showToast(true, "İş ilanı güncellendi.");
      setEditModal(false);
      fetchItems();
    } catch (err) {
      console.error(err);
      showToast(false, "Güncelleme sırasında hata oluştu.");
    } finally {
      setProcessing(p => ({ ...p, [editItem.Id + "save"]: false }));
    }
  };

  const statusBadge = (item) => {
    if (item.IsDeleted) return <Badge color="danger">Silinmiş</Badge>;
    if (item.IsActive === false) return <Badge color="warning">Pasif</Badge>;
    return <Badge color="success">Aktif</Badge>;
  };

  const columns = useMemo(() => [
    { Header: "ID", accessor: "Id", width: "80px" },
    { Header: "Başlık", accessor: "PositionName" },
    { Header: "Şirket", accessor: "Companies", Cell: ({ row }) => row.original.Companies?.Name || "-" },
    { Header: "Şehir", accessor: "Cities", Cell: ({ row }) => row.original.Cities?.Name || "-" },
    { Header: "Meslek", accessor: "Professions", Cell: ({ row }) => row.original.Professions?.Name || "-" },
    { Header: "Durum", accessor: "status", disableSortBy: true, Cell: ({ row }) => statusBadge(row.original) },
    { Header: "Tarih", accessor: "CreatedAt", Cell: ({ value }) => value ? moment(value).format("DD.MM.YYYY") : "-" },
    {
      Header: "İşlemler",
      accessor: "actions",
      disableSortBy: true,
      Cell: ({ row }) => (
        <div className="d-flex gap-2">
          <Button size="sm" color="info" onClick={() => openDetail(row.original)}>Detay</Button>
          <Button size="sm" color="primary" onClick={() => openEdit(row.original)}>Düzenle</Button>
          <Button size="sm" color="danger" disabled={processing[row.original.Id + "delete"]} onClick={() => handleDelete(row.original.Id)}>
            {processing[row.original.Id + "delete"] ? <Spinner size="sm" /> : "Sil"}
          </Button>
        </div>
      )
    }
  ], [processing]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="İş İlanları" breadcrumbItem="Liste" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <Button size="sm" color={filter === "all" ? "primary" : "light"} onClick={() => setFilter("all")}>Tümü</Button>
                    <Button size="sm" color={filter === "active" ? "success" : "light"} onClick={() => setFilter("active")}>Aktif</Button>
                    <Button size="sm" color={filter === "passive" ? "warning" : "light"} onClick={() => setFilter("passive")}>Pasif</Button>
                    <Button size="sm" color={filter === "deleted" ? "danger" : "light"} onClick={() => setFilter("deleted")}>Silinmiş</Button>
                  </div>
                  {loading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={items}
                      isGlobalFilter={true}
                      isAddOptions={false}
                      customPageSize={10}
                      className="custom-header-css"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={detailModal} toggle={() => setDetailModal(false)} size="lg">
        <ModalHeader toggle={() => setDetailModal(false)}>İş İlanı Detayı</ModalHeader>
        <ModalBody>
          {detailItem && (
            <div>
              <p><strong>ID:</strong> {detailItem.Id}</p>
              <p><strong>Başlık:</strong> {detailItem.PositionName}</p>
              <p><strong>Şirket:</strong> {detailItem.Companies?.Name || "-"}</p>
              <p><strong>Şehir:</strong> {detailItem.Cities?.Name || "-"}</p>
              <p><strong>Meslek:</strong> {detailItem.Professions?.Name || "-"}</p>
              <p><strong>İçerik:</strong> {detailItem.Description || "-"}</p>
              <p><strong>Durum:</strong> {statusBadge(detailItem)}</p>
              <p><strong>Tarih:</strong> {detailItem.CreatedAt ? moment(detailItem.CreatedAt).format("DD.MM.YYYY HH:mm") : "-"}</p>
            </div>
          )}
        </ModalBody>
      </Modal>

      <Modal isOpen={editModal} toggle={() => setEditModal(false)} size="lg">
        <ModalHeader toggle={() => setEditModal(false)}>İş İlanı Düzenle</ModalHeader>
        <ModalBody>
          {editItem && (
            <Form>
              <FormGroup><Label>Başlık</Label><Input type="text" value={editForm.PositionName} onChange={e => setEditForm({...editForm, PositionName: e.target.value})} /></FormGroup>
              <FormGroup><Label>Şirket</Label><Input type="text" value={editForm.CompanyName} onChange={e => setEditForm({...editForm, CompanyName: e.target.value})} /></FormGroup>
              <FormGroup><Label>Şehir</Label><Input type="text" value={editForm.City} onChange={e => setEditForm({...editForm, City: e.target.value})} /></FormGroup>
              <FormGroup><Label>Meslek</Label><Input type="text" value={editForm.Profession} onChange={e => setEditForm({...editForm, Profession: e.target.value})} /></FormGroup>
              <FormGroup><Label>İçerik</Label><Input type="textarea" rows={4} value={editForm.Description} onChange={e => setEditForm({...editForm, Description: e.target.value})} /></FormGroup>
              <FormGroup check><Label check><Input type="checkbox" checked={editForm.IsActive} onChange={e => setEditForm({...editForm, IsActive: e.target.checked})} /> Aktif</Label></FormGroup>
              <FormGroup check><Label check><Input type="checkbox" checked={editForm.IsDeleted} onChange={e => setEditForm({...editForm, IsDeleted: e.target.checked})} /> Silinmiş</Label></FormGroup>
              <Button color="primary" disabled={processing[editItem.Id + "save"]} onClick={handleSaveEdit}>
                {processing[editItem.Id + "save"] ? <Spinner size="sm" /> : "Kaydet"}
              </Button>
            </Form>
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default withRouter(JobPostsList);
